interface OverallScore {
  score: string;
  score_change: string;
  title: string;
}

interface GraphScore {
  metrics: {
    Testability: string;
    Readability: string;
    Maintainability: string;
    Security: string;
    Robustness: string;
    Scalability: string;
  };
}

interface CardScore {
  value: string;
  title: string;
  percentage_change: string;
}

interface PerformanceCollapsible {
  title: string;
  content: string;
  actions?: {
    title: string;
    content: string;
  }[];
}

// Parse overall score sections
const parseOverallScore = (data: string): string => {
  try {
    const match = data.match(/<overall-score>(.*?)<\/overall-score>/);
    if (!match) return '';

    const scoreData: OverallScore = JSON.parse(match[1]);
    return `## ${scoreData.title}\n\n**${scoreData.score}**${scoreData.score_change !== 'N/A' ? ` (${scoreData.score_change})` : ''}\n\nDescription: ${getScoreDescription(scoreData.title, scoreData.score)}\n\n`;
  } catch (error) {
    console.error('Error parsing overall score:', error);
    return '';
  }
};

// Parse graph score section
const parseGraphScore = (data: string): string => {
  try {
    const match = data.match(/<graph-score>(.*?)<\/graph-score>/);
    if (!match) return '';

    const graphData: GraphScore = JSON.parse(match[1]);
    let markdown = '## Code Quality Metrics\n\n';

    Object.entries(graphData.metrics).forEach(([metric, score]) => {
      markdown += `### ${metric}: **${score}/100**\n${getMetricDescription(metric)}\n\n`;
    });

    return markdown;
  } catch (error) {
    console.error('Error parsing graph score:', error);
    return '';
  }
};

// Parse card score sections
const parseCardScore = (data: string): string => {
  try {
    const matches = data.matchAll(/<card-score>(.*?)<\/card-score>/g);
    let markdown = '## Performance Metrics\n\n';

    if (matches) {
      Array.from(matches).forEach((match) => {
        const cardData: CardScore = JSON.parse(match[1]);
        markdown += `### ${cardData.title}\n**${cardData.value}**${cardData.percentage_change !== 'N/A' ? ` (${cardData.percentage_change})` : ''}\n${getCardDescription(cardData.title)}\n\n`;
      });
    }

    return markdown;
  } catch (error) {
    console.error('Error parsing card score:', error);
    return '';
  }
};

// Robust JSON parsing function (same as in Scores.tsx)
function robustJsonParse(str: string) {
  // Try direct parse first
  try {
    return JSON.parse(str);
  } catch (error) {
    // If direct parse fails, try with basic escaping
    try {
      const fixed = str
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
      return JSON.parse(fixed);
    } catch (error) {
      // Try unescaping over-escaped quotes and backslashes
      try {
        const unescaped = str.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
        return JSON.parse(unescaped);
      } catch (error) {
        // Try comprehensive control character escaping
        try {
          const comprehensiveFixed = str
            .replace(/\n/g, '\\n')
            .replace(/\r/g, '\\r')
            .replace(/\t/g, '\\t')
            .replace(/[\x00-\x1F\x7F]/g, (char) => {
              const code = char.charCodeAt(0);
              return `\\u${code.toString(16).padStart(4, '0')}`;
            });
          return JSON.parse(comprehensiveFixed);
        } catch (error) {
          // If all parsing attempts fail, return empty array
          return [];
        }
      }
    }
  }
}

// Parse performance collapsible sections
const parsePerformanceCollapsible = (data: string): string => {
  try {
    const match = data.match(
      /<performance-collapsible>(.*?)<\/performance-collapsible>/
    );
    if (!match) {
      return '';
    }

    const sections: PerformanceCollapsible[] = robustJsonParse(match[1]);

    let markdown = '';

    sections.forEach((section) => {
      // Replace bullet points with dashes in the content
      let cleanContent = section.content || '';
      cleanContent = cleanContent
        .replace(/\u2022/g, '-') // Replace bullet point with dash
        .replace(/\u2023/g, '-') // Replace triangular bullet with dash
        .replace(/\u25E6/g, '-') // Replace white bullet with dash
        .replace(/\u2043/g, '-') // Replace hyphen bullet with dash
        .replace(/\u2219/g, '-'); // Replace bullet operator with dash

      markdown += `## ${section.title}\n\n${cleanContent}\n\n`;

      if (section.actions) {
        section.actions.forEach((action) => {
          // Also replace bullet points in action content
          let cleanActionContent = action.content || '';
          cleanActionContent = cleanActionContent
            .replace(/\u2022/g, '-') // Replace bullet point with dash
            .replace(/\u2023/g, '-') // Replace triangular bullet with dash
            .replace(/\u25E6/g, '-') // Replace white bullet with dash
            .replace(/\u2043/g, '-') // Replace hyphen bullet with dash
            .replace(/\u2219/g, '-'); // Replace bullet operator with dash

          markdown += `### ${action.title}\n${cleanActionContent}\n\n`;
        });
      }
    });

    return markdown;
  } catch (error) {
    console.error('Error parsing performance collapsible:', error);
    return '';
  }
};

// Helper function to get score descriptions
const getScoreDescription = (title: string, score: string): string => {
  const descriptions: Record<string, string> = {
    'Overall Score':
      'Represents the comprehensive evaluation of performance across all metrics, including code quality, productivity, and impact.',
    'Story Points':
      'Indicates the total amount of work completed, measured in story points which represent complexity and effort.',
  };
  return descriptions[title] || 'No description available';
};

// Helper function to get metric descriptions
const getMetricDescription = (metric: string): string => {
  const descriptions: Record<string, string> = {
    Testability:
      'Measures how easily the code can be tested, including unit tests coverage and test infrastructure.',
    Readability:
      'Evaluates how easy it is to understand the code, including naming conventions and code structure.',
    Maintainability:
      'Assesses how easily the code can be modified or extended without introducing bugs.',
    Security:
      "Measures the code's resistance to security vulnerabilities and adherence to security best practices.",
    Robustness: 'Evaluates how well the code handles errors and edge cases.',
    Scalability:
      'Assesses how well the code can handle increased load and growing requirements.',
  };
  return descriptions[metric] || 'No description available';
};

// Helper function to get card descriptions
const getCardDescription = (title: string): string => {
  const descriptions: Record<string, string> = {
    'Merge Frequency':
      'The average number of pull requests merged per week, indicating development velocity.',
    'Bug Ratio':
      'The Bug Ratio analyzes issues found during code review and testing. A lower percentage indicates fewer potential bugs. Considers code review findings, edge cases, and error-prone code paths.',
    'PR Size':
      'PR Size shows total PRs created in the last 90 days. While higher numbers show activity, focus on delivering value through well-sized, quality changes rather than quantity.',
  };
  return descriptions[title] || 'No description available';
};

// Main function to convert review data to markdown
export const convertReviewToMarkdown = (reviewData: string): string => {
  let markdown = '# Performance Review\n\n';

  // Parse each section
  const overallScoreMarkdown = parseOverallScore(reviewData);
  const graphScoreMarkdown = parseGraphScore(reviewData);
  const cardScoreMarkdown = parseCardScore(reviewData);
  const performanceMarkdown = parsePerformanceCollapsible(reviewData);

  markdown += overallScoreMarkdown;
  markdown += graphScoreMarkdown;
  markdown += cardScoreMarkdown;
  markdown += performanceMarkdown;

  return markdown;
};

const containsMarkdown = (text: string): boolean => {
  const markdownPatterns = [
    /[*_]{1,2}[^*_]+[*_]{1,2}/, // bold/italic
    /\[([^\]]+)\]\(([^)]+)\)/, // links
    /^#{1,6}\s/, // headers
    /^[-*+]\s/, // unordered lists
    /^>\s/, // blockquotes
    /`[^`]+`/, // inline code
    /```[\s\S]*?```/, // code blocks
    /^\d+\.\s/, // ordered lists
    /\|\s*[-:]+\s*\|/, // tables
  ];

  return markdownPatterns.some((pattern) => pattern.test(text));
};

/**
 * Strip markdown formatting from text and return plain text
 */
export const stripMarkdown = (text: string): string => {
  if (!text) return '';

  return (
    text
      // Remove code blocks
      .replace(/```[\s\S]*?```/g, '')
      // Remove inline code
      .replace(/`([^`]+)`/g, '$1')
      // Review-bot scaffolding is delimited by marker pairs. Drop the wrapped
      // content, not just the markers, or the embedded fix prompt leaks into the
      // plain text. Must run before the generic HTML-comment rule below.
      .replace(
        /<!--\s*ai_prompt_start\s*-->[\s\S]*?<!--\s*ai_prompt_end\s*-->/gi,
        ''
      )
      .replace(
        /<!--\s*suggestion_start\s*-->[\s\S]*?<!--\s*suggestion_end\s*-->/gi,
        ''
      )
      // Remove any remaining standalone HTML comments
      .replace(/<!--[\s\S]*?-->/g, '')
      // Drop collapsed <details> sections wholesale — their body is tooling
      // scaffolding (e.g. "Prompt to fix with AI"), not the comment itself.
      .replace(/<details[\s\S]*?<\/details>/gi, '')
      // Images must be dropped BEFORE links: ![alt](url) contains the
      // [text](url) shape, so stripping links first leaves a bare "!alt"
      // behind. Badge/screenshot alt text is not readable prose, so the whole
      // image goes rather than keeping $1.
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      // Remove links [text](url)
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove any remaining HTML tags, keeping their text content
      .replace(/<[^>]+>/g, ' ')
      // Remove headers (e.g., # Header, ## Header)
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold/italic (**, *, __, _)
      .replace(/[*_]{1,2}([^*_]+)[*_]{1,2}/g, '$1')
      // Remove blockquotes
      .replace(/^>\s+/gm, '')
      // Remove horizontal rules
      .replace(/^[-*_]{3,}$/gm, '')
      // Remove unordered list markers
      .replace(/^[-*+]\s+/gm, '')
      // Remove ordered list markers
      .replace(/^\d+\.\s+/gm, '')
      // Remove extra whitespace and trim
      .replace(/\s+/g, ' ')
      .trim()
  );
};

/**
 * Shorten a title to a maximum length, adding ellipsis if needed
 */
export const shortenTitle = (title: string, maxLength: number = 60): string => {
  if (!title) return '';
  const cleaned = stripMarkdown(title);
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength).trim() + '...';
};
